/**
 * useAstrologerChat — the AI-astrologer chat state machine.
 *
 *   new chat:  GET /api/v1/chat/opener?chartId=&surface=&seedRef=&lang=  (nothing is written; asked once)
 *              first POST /api/v1/chat/message = the same query object + text → `event: conversation`
 *   a thread:  GET /api/v1/chat/threads/:id, then POST /chat/message { conversationId, text, lang }
 *
 * Money path rules (server contract B1, «Final HTTP contract»):
 * - the id from `event: conversation` is kept at once, so a retry after a failed or dropped first
 *   answer goes into the SAME conversation (no duplicate thread);
 * - `insufficient_stars` before any answer text → the question is taken back and resent after a
 *   top-up (to the conversation if one was created); after answer text → the answer was stored and
 *   not charged: it stays on screen and nothing is resent;
 * - a dropped stream (`network`) with a known conversation: the server finishes and CHARGES the answer
 *   even when the client dropped, and stores it in one row at the end. So the stored thread is re-read
 *   every RECONCILE_POLL_MS for up to RECONCILE_WAIT_MS; once the answer is there it is shown and
 *   nothing is offered for retry. A retry after `network` / `in_flight` reads the thread once more first
 *   ("answered" = `isAnswered` in lib/chat-sync: by count of questions, never by text);
 * - a question rejected before it was stored (NOT_STORED) is `unsent`: not counted, dropped on the next send
 *   (the text stays for «Повторить»); `stream_error` with a known conversation reads the thread once;
 * - GONE codes (the chart was deleted) stop the chat and refresh the chart caches;
 * - after a paid top-up the resend waits (up to STAR_CREDIT_WAIT_MS) until the wallet shows the new
 *   stars — the bot credits them on `successful_payment`, which can lag behind the `paid` callback;
 * - an abort (unmount / another target) never shows an error.
 * Never logs message text, names or birth data.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api-client';
import { ensureOk } from '../lib/api-error';
import { type ChatErrorCode } from '../lib/chat-sse';
import {
  ASK_ONCE, cachedSex,
  pickLang, toHeader,
  type ChatHeader, type ChatLoadError, type ChatMsg, type OpenerResponse, type ThreadResponse,
} from '../lib/chat-sync';
import { useAuth } from '../store/auth';

import { useChatSession } from './useChatSession';
import { RECONCILE_POLL_MS, RECONCILE_WAIT_MS, STAR_CREDIT_POLL_MS, STAR_CREDIT_WAIT_MS } from '../lib/chat-sync';

export type { ChatErrorCode, ChatMsg, ChatHeader, ChatLoadError };
export type ChatTarget =
  | { kind: 'new'; chartId: string | null; surface: string; seedRef: string | null }
  | { kind: 'thread'; threadId: string };
export { STAR_CREDIT_POLL_MS, STAR_CREDIT_WAIT_MS, RECONCILE_POLL_MS, RECONCILE_WAIT_MS };

export interface UseAstrologerChat {
  redirectCourseThreadId?: string;
  loadState: 'loading' | 'ready' | 'error';
  loadError: ChatLoadError | null;
  header: ChatHeader | null;
  messages: ChatMsg[];
  /** New conversation, opener loaded, no user message yet. */
  canShowChips: boolean;
  /** True from send until the answer is final — also while a dropped stream is checked against the stored thread. */
  streaming: boolean;
  /** Last send error (never `insufficient_stars` — that sets `needTopUp`). chart_gone / chart_not_found / not_found stop the chat. */
  error: ChatErrorCode | null;
  needTopUp: boolean;
  /** After a paid top-up: waiting until the wallet shows the stars, then the question is resent. */
  waitingForStars: boolean;
  /** Ignored while streaming, waiting for stars, before the opener/thread is loaded, or after the chart is gone. */
  send: (text: string) => void;
  /** Resends the last unanswered question (into the known conversation, if any). */
  retry: () => void;
  /**
   * Closes the top-up sheet. If `insufficient_stars` took the question back off the screen (no answer
   * text came), returns that text and forgets it (put it back into the composer); otherwise null.
   */
  dismissTopUp: () => string | null;
  /**
   * After a paid top-up: closes the sheet; if `insufficient_stars` opened it and the question is still
   * pending, waits for the stars (`waitingForStars`) and resends it. Resolves when that is done.
   */
  reloadAfterTopUp: () => Promise<void>;
  /** Asks for the opener / thread again after a load error. */
  reload: () => void;
  /**
   * The chart of this conversation once known (a thread's chart, or the one asked about; null = none /
   * the own chart). Optional so that screen tests with a hand-made state still type-check.
   */
  chartId?: string | null;
  /** The saved conversation shown or started here (null until the first answer of a new chat begins). */
  conversationId?: string | null;
}

export function useAstrologerChat(target: ChatTarget): UseAstrologerChat {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  const { i18n } = useTranslation();
  const lang = pickLang(i18n.language);

  const isNew = target.kind === 'new';
  const chartId = target.kind === 'new' ? target.chartId || null : null;
  const surface = target.kind === 'new' ? target.surface : '';
  const seedRef = target.kind === 'new' ? target.seedRef || null : null;
  const threadId = target.kind === 'thread' ? target.threadId : null;
  const targetKey = isNew ? `new|${chartId ?? ''}|${surface}|${seedRef ?? ''}` : `thread|${threadId}`;

  const opener = useQuery<OpenerResponse, Error>({
    queryKey: ['chat-opener', chartId ?? '', surface, seedRef ?? '', lang],
    enabled: !!jwt && isNew,
    ...ASK_ONCE,
    // Not kept after the screen closes: every entry into the chat asks again (= one `chat_opened`).
    gcTime: 0,
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (chartId) qs.set('chartId', chartId);
      if (surface) qs.set('surface', surface);
      if (seedRef) qs.set('seedRef', seedRef);
      qs.set('lang', lang);
      const res = await ensureOk(await apiFetch(`/api/v1/chat/opener?${qs.toString()}`, jwt as string));
      return (await res.json()) as OpenerResponse;
    },
  });
  const thread = useQuery<ThreadResponse, Error>({
    queryKey: ['chat-thread', threadId ?? ''],
    enabled: !!jwt && !isNew && !!threadId,
    ...ASK_ONCE,
    queryFn: async () => {
      const res = await ensureOk(
        await apiFetch(`/api/v1/chat/threads/${encodeURIComponent(threadId as string)}`, jwt as string),
      );
      return (await res.json()) as ThreadResponse;
    },
  });

  // What the server gave us, before the user says anything.
  let base: ChatMsg[] | null = null;
  let loadedHeader: ChatHeader | null = null;
  let knownChartId: string | null = null;
  if (isNew && opener.data) {
    base = [{ role: 'assistant', content: opener.data.opener }];
    loadedHeader = toHeader(opener.data.chart, true);
    knownChartId = chartId ?? opener.data.chart?.id ?? null;
  } else if (!isNew && thread.data && thread.data.surface !== 'course') {
    base = thread.data.messages.map(({ role, content }) => ({ role, content }));
    const tc = thread.data.chart;
    loadedHeader = toHeader(tc && !tc.isSelf ? { ...tc, sex: tc.sex ?? cachedSex(qc, tc.id) } : tc, true);
    knownChartId = thread.data.chartId;
  }
  const query = isNew ? opener : thread;

  const session = useChatSession({ targetKey, base, loadedHeader, knownChartId, chartId, threadId, isNew, query, jwt, lang, surface, seedRef });
  return { ...session, ...(thread.data?.surface === 'course' && threadId ? { redirectCourseThreadId: threadId } : {}) };
}
